<template>
  <ChapterCardTemplate
    :chapter="chapter.attributes.chapter"
    :volume="chapter.attributes.volume"
    :name="chapter.attributes.name"
    :publishAt="chapter.attributes.publishAt ?? chapter.attributes.createdAt"
    :volumeKind
    :chapterKind
    :likes="!noStats ? (stats?.attributes.reactions.like ?? 0) : undefined"
    :comments="!noStats ? commentCount : undefined"
    :views="!noStats ? (stats?.attributes.viewCount ?? 0) : undefined"
    :statsPending="isPending"
    :thumbnail-url="thumbnailUrl || titleCoverUrl"
    :href="chapterHasLink ? linkTo(chapter) : undefined"
    :language="!noLang ? chapter.attributes.translatedLanguage : undefined"
    :isRead="_isRead || isRead"
    :isLocked
    :isAccess
    :transparent
    :small
    :xSmall
    @click="handleChapterClick"
  >
    <template #details v-if="chapter.attributes.description && !selecting">
      <p>{{ chapter.attributes.description }}</p>
    </template>
    <template #contextMenu v-if="!selecting">
      <slot name="contextMenu">
        <ContextMenuList
          close-on-outside-click
          default-position="bottom-left"
          class="inline-block relative"
          :options="
            [
              {
                icon: isRead ? IconEyeOff : IconEye,
                name: isRead
                  ? translate('components.chapterCard.contextMenuMarkUnread')
                  : translate('components.chapterCard.contextMenuMarkRead'),
                action: () => {
                  if (!loggedIn) appStore?.openLoginRequiredModal();
                  else toggleRead();
                },
              },
              selectable && {
                icon: IconSelectAll,
                name: translate('components.chapterCard.contextMenuSelectAll'),
                action: () => {
                  if (!loggedIn) appStore?.openLoginRequiredModal();
                  else emit('selectAll');
                },
              },
            ].filter(onlyTruthys)
          "
        >
          <template v-slot="{ isOpen, open, close }">
            <NamiButton
              text
              :icon="IconDots"
              button-type="secondary"
              small
              class="w-6 h-6"
              @button-click="() => (isOpen ? close() : open())"
            />
          </template>
        </ContextMenuList>
      </slot>
    </template>
  </ChapterCardTemplate>
</template>

<script setup lang="ts">
import {
  getNovelChapterThumbnail,
  getRelationship,
  isRelationship,
  isTitle,
  type NovelChapterEntity,
  type NovelChapterRelation,
  type OrganizationEntity,
  type OrganizationRelation,
  type PopulateRelationship,
  Title,
} from "~/src/api";
import type { ChapterCardTemplateStyleProps } from "~/components/chapter/ChapterCardTemplate.vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  IconDots,
  IconEyeOff,
  IconSelectAll,
  IconEye,
} from "@tabler/icons-vue";
import ModalGatingNoAccess from "~/components/modal/gating/ModalGatingNoAccess.vue";
import { promptMature } from "~/src/reader/ux";

const {
  chapter,
  organization,
  isMature,
  isLocked,
  isAccess,
  selecting,
  titleId: extTitleId,
} = defineProps<
  {
    titleId?: string;
    organization?:
      | OrganizationEntity
      | PopulateRelationship<OrganizationRelation>;

    noStats?: boolean;
    isMature?: boolean;
    selectable?: boolean;

    chapter: NovelChapterEntity | PopulateRelationship<NovelChapterRelation>;
    titleCoverUrl?: string;
    selecting?: boolean;
    noLang?: boolean;
  } & ChapterCardTemplateStyleProps
>();

const emit = defineEmits<{
  (e: "navigate", id: string): void;
  (e: "selectAll"): void;
}>();

if (isRelationship(chapter) && !organization) {
  throw new Error(
    "NovelChapterCard: must provide an organization when chapter is a relationship",
  );
}

const titleId = computed(
  () =>
    (chapter as NovelChapterEntity).relationships?.find(isTitle)?.id ??
    extTitleId ??
    "",
);

const { data: stats, isPending } = useQuery(
  useNovelChapterStats(computed(() => chapter.id)),
);

const commentCount = computed(() => {
  const chapterComments = stats.value?.attributes.commentCount ?? 0;
  const paragraphComments = sum(
    Object.values(
      stats.value?.attributes.extra.novelParagraphStatistics ?? {},
    ).map((para) => para.commentCount),
  );
  return chapterComments + paragraphComments;
});

const thumbnailUrl = computed(() => getNovelChapterThumbnail(chapter, "256"));
const mappedChapterLanguage = computed(() =>
  reverseLocaleMap(chapter.attributes.translatedLanguage),
);

// read markers
const queryClient = useQueryClient();
const { markerQuery, markerMutation } = useNovelReadMarkerManager(
  queryClient,
  titleId,
);
const { data: readMarkers } = markerQuery;
const { mutate: updateReadMarkers } = markerMutation;
const _isRead = computed(() => !!readMarkers.value?.[chapter.id]);
const toggleRead = () => {
  if (_isRead.value) updateReadMarkers({ remove: [chapter.id] });
  else updateReadMarkers({ add: [chapter.id] });
};

// functional, mature/gated
const route = useRoute();
const nuxtApp = useNuxtApp();
const { t: translate } = nuxtApp.$i18n.global;

const authStore = useAuthStore();
const appStore = useAppStore();

const settingsStore = nuxtApp.$settings();
const loggedIn = useIsLoggedIn();

const chapterHasLink = computed(() => {
  if (isLocked && !isAccess) return false;
  if (userNotMature.value) return false;
  if (selecting) return false;
  return true;
});

async function showUserHasNoAccessToChapterModal(): Promise<
  "subscribe" | "cancel"
> {
  return (
    (await appStore?.prompt(
      translate("components.chapterCard.promptSubscriptionRequiredText"),
      {
        icon: "okay",
        forceCloseKey: "cancel",
        buttons: {
          cancel: {
            buttonType: "secondary",
            buttonText: translate(
              "components.chapterCard.promptSubscriptionRequiredButtonCancel",
            ),
          },
          subscribe: {
            buttonType: "primary",
            buttonText: translate(
              "components.chapterCard.promptSubscriptionRequiredButtonSubscribe",
            ),
          },
        },
      },
    )) ?? "cancel"
  );
}

async function showUserHasNoAccessToChapterModalNew() {
  if (!titleId.value || !organization) {
    await showUserHasNoAccessToChapterModal();
  } else {
    await appStore?.prompt(
      ModalGatingNoAccess,
      { large: true },
      {
        titleId: titleId.value,
        orgId: organization.id,
        orgSlug: organization.attributes.slug,
      },
    );
  }
  return "";
}

async function promptUserSubscription() {
  const result = await showUserHasNoAccessToChapterModalNew();
  if (result !== "subscribe") return;

  const org = await getChapterOrganization();
  navigateTo({
    path: `${linkTo(org)}/subscriptions`,
    query: {
      returnTo: encodeURIComponent(route.fullPath),
    },
  });
}

async function getChapterOrganization() {
  if (organization) return organization;
  if (titleId.value) {
    const fetchedTitle = await Title.get(titleId.value, ["organization"]);
    return getRelationship(fetchedTitle, "organization", true);
  }
  throw new Error("Not enough information provided to get organization.");
}

const userNotMature = computed(
  () => isMature && !settingsStore?.settings.platform.matureContentEnabled,
);

async function handleChapterClick(e: Event) {
  if (!isAccess && isLocked) {
    e.preventDefault();
    e.stopPropagation();
    if (!loggedIn.value) {
      appStore.openLoginRequiredModal();
      return;
    }
    await promptUserSubscription();
  }

  if (userNotMature.value) {
    e.preventDefault();
    e.stopPropagation();

    const result = await promptMature(false);
    if (result === "cancel") return;

    if (loggedIn.value)
      return navigateTo({
        path: `/${mappedChapterLanguage.value}/settings/account-preferences`,
        query: {
          returnTo: encodeURIComponent(route.fullPath),
        },
      });
    return authStore.login({
      redirectBackTo: route.fullPath,
      locale: mappedChapterLanguage.value,
    });
  }

  emit("navigate", chapter.id);
}
</script>
